import { useCallback, useState, useEffect } from 'react';
import { type ReceivedMessage, useSessionContext, useSessionMessages } from '@livekit/components-react';

export interface TranscriptEntry {
  id: string;
  speaker: 'agent' | 'user';
  text: string;
  timestamp: number;
}

interface UseTranscriptCaptureReturn {
  transcript: TranscriptEntry[];
  sessionStartTime: number | null;
  getDuration: () => number;
  clearTranscript: () => void;
}

/**
 * Hook to capture messages from a LiveKit session and convert them to transcript format.
 *
 * IMPORTANT: LiveKit streams transcription messages token-by-token with the same message ID.
 * This hook uses an UPDATE strategy - when a message with the same ID arrives with longer text,
 * it updates the existing entry instead of creating a duplicate or blocking the update.
 */
export function useTranscriptCapture(): UseTranscriptCaptureReturn {
  const session = useSessionContext();
  const { messages } = useSessionMessages(session);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);

  // Set session start time when first message arrives
  useEffect(() => {
    if (messages.length > 0 && sessionStartTime === null) {
      setSessionStartTime(Date.now());
    }
  }, [messages, sessionStartTime]);

  // Process messages - UPDATE existing entries instead of deduplicating
  useEffect(() => {
    if (!sessionStartTime) return;

    setTranscript((prev) => {
      // Create a map from existing entries for quick lookup
      const entryMap = new Map(prev.map((e) => [e.id, e]));

      messages.forEach((msg: ReceivedMessage) => {
        // Skip if no text content
        if (!msg.message || typeof msg.message !== 'string') return;

        const existingEntry = entryMap.get(msg.id);

        if (existingEntry) {
          // UPDATE: Replace with longer/newer text (streaming updates have progressively longer text)
          if (msg.message.length > existingEntry.text.length) {
            entryMap.set(msg.id, {
              ...existingEntry,
              text: msg.message,
            });
          }
        } else {
          // NEW: Add new entry
          entryMap.set(msg.id, {
            id: msg.id,
            speaker: msg.from?.isLocal ? 'user' : 'agent',
            text: msg.message,
            timestamp: msg.timestamp ? msg.timestamp - sessionStartTime : Date.now() - sessionStartTime,
          });
        }
      });

      // Convert map back to array, sorted by timestamp
      return Array.from(entryMap.values()).sort((a, b) => a.timestamp - b.timestamp);
    });
  }, [messages, sessionStartTime]);

  const getDuration = useCallback(() => {
    if (!sessionStartTime) return 0;
    return Math.round((Date.now() - sessionStartTime) / 1000);
  }, [sessionStartTime]);

  const clearTranscript = useCallback(() => {
    setTranscript([]);
    setSessionStartTime(null);
  }, []);

  return {
    transcript,
    sessionStartTime,
    getDuration,
    clearTranscript,
  };
}
